/**
 * Event Types and Schema for Captain Mesh Domain Protocol
 */
import { uuid } from "@/lib/uuid";

export const EventTypes = {
  ORDER_STARTED: "ORDER_STARTED",
  CUSTOMER_ASSOCIATED: "CUSTOMER_ASSOCIATED",
  ITEM_ADDED: "ITEM_ADDED",
  ITEM_QUANTITY_DELTA: "ITEM_QUANTITY_DELTA",
  ITEM_REMOVED: "ITEM_REMOVED",
  // A line's special note, set after the line was punched: {line_id, notes}.
  // Blank notes clears it. Reconciled server-side by mesh_reconciliation_service.
  ITEM_NOTE_SET: "ITEM_NOTE_SET",
  MODIFIER_ADDED: "MODIFIER_ADDED",
  MODIFIER_REMOVED: "MODIFIER_REMOVED",
  ORDER_NOTE_CHANGED: "ORDER_NOTE_CHANGED",
  TABLE_ORDER_TRANSFERRED: "TABLE_ORDER_TRANSFERRED",
  KOT_REQUESTED: "KOT_REQUESTED",
};

/**
 * Creates a standard event envelope.
 */
export function createEvent({
  deviceId,
  deviceSequence,
  aggregateType = "ORDER",
  aggregateId,
  parentRevision,
  resultRevision,
  type,
  payload,
  causationId = null,
  parentEventId = null,
}) {
  return {
    schema_version: 1,
    event_id: uuid(),
    device_id: deviceId,
    device_sequence: deviceSequence,
    aggregate_type: aggregateType,
    aggregate_id: aggregateId,
    parent_revision: parentRevision,
    result_revision: resultRevision,
    type,
    payload,
    created_at: new Date().toISOString(),
    causation_id: causationId,
    parent_event_id: parentEventId,
  };
}

export function createOrderStartedEvent(deviceId, seq, tableId, aggregateId) {
  return createEvent({
    deviceId,
    deviceSequence: seq,
    aggregateId,
    parentRevision: 0,
    resultRevision: 1,
    type: EventTypes.ORDER_STARTED,
    payload: { table_id: tableId },
  });
}

export function createCustomerAssociatedEvent(deviceId, seq, orderId, parentRev, customer, parentEventId) {
  return createEvent({
    deviceId,
    deviceSequence: seq,
    aggregateId: orderId,
    parentRevision: parentRev,
    resultRevision: parentRev + 1,
    type: EventTypes.CUSTOMER_ASSOCIATED,
    payload: { customer },
    parentEventId,
  });
}

export function createItemAddedEvent(deviceId, seq, orderId, parentRev, lineId, menuItemId, quantity, parentEventId) {
  return createEvent({
    deviceId,
    deviceSequence: seq,
    aggregateId: orderId,
    parentRevision: parentRev,
    resultRevision: parentRev + 1,
    type: EventTypes.ITEM_ADDED,
    payload: { line_id: lineId, menu_item_id: menuItemId, quantity },
    parentEventId,
  });
}

export function createItemQuantityDeltaEvent(deviceId, seq, orderId, parentRev, lineId, delta, parentEventId) {
  return createEvent({
    deviceId,
    deviceSequence: seq,
    aggregateId: orderId,
    parentRevision: parentRev,
    resultRevision: parentRev + 1,
    type: EventTypes.ITEM_QUANTITY_DELTA,
    payload: { line_id: lineId, delta },
    parentEventId,
  });
}

export function createItemRemovedEvent(deviceId, seq, orderId, parentRev, lineId, parentEventId) {
  return createEvent({
    deviceId,
    deviceSequence: seq,
    aggregateId: orderId,
    parentRevision: parentRev,
    resultRevision: parentRev + 1,
    type: EventTypes.ITEM_REMOVED,
    payload: { line_id: lineId },
    parentEventId,
  });
}

export function createKotRequestedEvent(deviceId, seq, orderId, parentRev, itemsToKot, parentEventId) {
  // Idempotency key to safely collapse concurrent KOT intents for the same items
  // Format: orderId:revision
  const intentId = `${orderId}:${parentRev}`;
  return createEvent({
    deviceId,
    deviceSequence: seq,
    aggregateId: orderId,
    parentRevision: parentRev,
    resultRevision: parentRev + 1,
    type: EventTypes.KOT_REQUESTED,
    payload: { intent_id: intentId, items: itemsToKot },
    parentEventId,
  });
}
